import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';

import { AUTH_SERVER_URL } from '../../config';
import { AddUserInfo, PersonRoleAndServiceRequest } from '../../shared/models/add-user-info';
import { DragDropServices } from '../models/user-service';
import { Users } from '../../users/models/user';

const AUTH_USERS = `${ AUTH_SERVER_URL }/v2/users`;
const ADD_USER_TO_ORG = `${ AUTH_SERVER_URL }/v2/organizations:external`;

@Injectable({ providedIn: 'root' })
export class AddUserService {
	firstName: Observable<string>;
	lastName: Observable<string>;
	email: Observable<string>;
	userType: Observable<string>;
	administrator: Observable<boolean>;
	services: Observable<DragDropServices[]>;
	orgId: Observable<string>;
	isNewUserCreation: Observable<boolean>;
	userId: Observable<string>;
	hideNextBtn: Observable<boolean>;
	isAnyOneSelected: Observable<boolean>;
	nodeSelected: Observable<number | undefined>;
	currentSelectedUser: Observable<Users | undefined>;
	emailIdExistsCheck: Observable<boolean>;
	private readonly emailIdExistsSubject = new BehaviorSubject(<boolean>false);
	private readonly firstNameSubject = new BehaviorSubject(<string>'');
	private readonly lastNameSubject = new BehaviorSubject(<string>'');
	private readonly emailSubject = new BehaviorSubject(<string>'');
	private readonly userTypeSubject = new BehaviorSubject(<string>'');
	private readonly orgIdSubject = new BehaviorSubject(<string>'');
	private readonly servicesSubject = new BehaviorSubject(<DragDropServices[]>[]);
	private readonly isNewUserCreationSubject = new BehaviorSubject(<boolean>false);
	private readonly userIdSubject = new BehaviorSubject(<string>'');
	private readonly hideNextBtnSubject = new BehaviorSubject(<boolean>false);
	private readonly administratorSubject = new BehaviorSubject(<boolean>false);
	private readonly isAnyOneSelectedSubject = new BehaviorSubject(<boolean>false);
	private readonly nodeSelectedSubject = new BehaviorSubject(<number | undefined>undefined);
	private readonly selectedUserSubject = new BehaviorSubject(<Users | undefined>{});
	constructor(
		private readonly http: HttpClient,
		private readonly router: Router
	) {
		this.firstName = this.firstNameSubject.asObservable();
		this.lastName = this.lastNameSubject.asObservable();
		this.email = this.emailSubject.asObservable();
		this.userType = this.userTypeSubject.asObservable();
		this.orgId = this.orgIdSubject.asObservable();
		this.services = this.servicesSubject.asObservable();
		this.isNewUserCreation = this.isNewUserCreationSubject.asObservable();
		this.userId = this.userIdSubject.asObservable();
		this.hideNextBtn = this.hideNextBtnSubject.asObservable();
		this.administrator = this.administratorSubject.asObservable();
		this.isAnyOneSelected = this.isAnyOneSelectedSubject.asObservable();
		this.nodeSelected = this.nodeSelectedSubject.asObservable();
		this.currentSelectedUser = this.selectedUserSubject.asObservable();
		this.emailIdExistsCheck = this.emailIdExistsSubject.asObservable();
	}

	updateUserId(userId: string) {
		this.userIdSubject.next(userId);
	}

	updateFirstName(firstName: string) {
		this.firstNameSubject.next(firstName);
	}

	updateLastName(lastName: string) {
		this.lastNameSubject.next(lastName);
	}

	updateEmail(email: string) {
		this.emailSubject.next(email);
	}

	updateUserType(userType: string) {
		this.userTypeSubject.next(userType);
	}

	updateServices(services: DragDropServices[]) {
		this.servicesSubject.next(services);
	}

	updateAdmin(administrator: boolean) {
		this.administratorSubject.next(administrator);
	}

	updateOrgId(orgId: string) {
		this.orgIdSubject.next(orgId);
	}

	updateIsNewUserCreation(isNewUser: boolean) {
		this.isNewUserCreationSubject.next(isNewUser);
	}

	updateNodeSelected(node: number | undefined) {
		this.nodeSelectedSubject.next(node);
	}

	updateIsAnyOneChoiceSelected(isAnyOneSelected: boolean) {
		this.isAnyOneSelectedSubject.next(isAnyOneSelected);
	}

	getCurrentRoute(): string {
		return this.router.url;
	}

	addUserToOrg(personRoleAndServiceRequest: PersonRoleAndServiceRequest, orgId: number, userId: number) {
		const url = new URL(`${ ADD_USER_TO_ORG }/organizations/:${ orgId }/users/:${ userId }`);
		return this.http.post(url.toString(), personRoleAndServiceRequest);
	}

	addUser(addUserInfo: AddUserInfo) {
		const url = new URL(AUTH_USERS);
		return this.http.post(url.toString(), addUserInfo);
	}

	setSelectedUser(currentSelectedUser: Users | undefined) {
		this.selectedUserSubject.next(currentSelectedUser);
	}

	async emailIdExists(emailId: string) {
		const url = new URL(`${ AUTH_SERVER_URL }/v2/email/${ emailId }`);
		return firstValueFrom(this.http.get<boolean>(url.toString()));
	}

	updateEmailIdExists(emialIdExists: boolean) {
		this.emailIdExistsSubject.next(emialIdExists);
	}
}
